import { createFileRoute } from "@tanstack/react-router";
/**
 * BulkOrder Review Page
 *
 * This page allows users to review the details of an bulk_suborder and add notes.
 *
 * The bulk_suborderId param is required.
 */
import { useSuspenseQuery } from "@tanstack/react-query";
import { suspenseOptions } from "@/lib/suspense-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getGetBulkSuborderQueryOptions } from "@/requests/gen/react-query/bulk-suborder";
import BulkSuborderContentsCard from "./-bulk-suborder-contents-card";
import BulkSuborderHistory from "./-bulk-suborder-history";
import BulkSuborderConfirmCard from "./-bulk-suborder-confirm-card";
import { Header } from "@/components/universal/header";
import BulkSuborderNoteForm from "./-bulk-suborder-notes-form";
import BulkSuborderActions from "./-bulk-suborder-actions";

export const Route = createFileRoute(
  "/_private/_fulfillment-360/bulk-suborder/$bulkSuborderId/",
)({
  loader: ({ context: { queryClient }, params: { bulkSuborderId } }) =>
    queryClient.ensureQueryData(getGetBulkSuborderQueryOptions(bulkSuborderId)),
  component: BulkSuborderReviewPage,
});

function BulkSuborderReviewPage() {
  const { bulkSuborderId } = Route.useParams();
  const bulk_suborder = useSuspenseQuery(
    suspenseOptions(getGetBulkSuborderQueryOptions(bulkSuborderId)),
  );

  const isCancelled = bulk_suborder.data.status === "Cancelled";

  return (
    <div>
      <title>{`Review Bulk Suborder ${bulkSuborderId} - Legrande Health Portal`}</title>
      <Header
        crumbs={[
          { name: "Bulk Suborders", link: { to: "/bulk-suborder/lookup" } },
          {
            name: "Review",
            link: {
              to: "/bulk-suborder/$bulkSuborderId",
              params: { bulkSuborderId },
            },
          },
        ]}
      >
        <BulkSuborderActions
          bulk_suborder={bulk_suborder.data}
          refetch={bulk_suborder.refetch}
        />
      </Header>
      {/* Main Content */}
      <main className="grid flex-1 items-start gap-4 xl:grid-cols-3">
        {/* Big Column */}
        <div className="grid gap-4 xl:col-span-2">
          {/* Contents Section */}
          <div className="relative">
            <BulkSuborderContentsCard
              bulk_suborder={bulk_suborder.data}
              refetch={bulk_suborder.refetch}
            />
            {isCancelled && (
              <div className="absolute inset-0 bg-order-cancelled z-50 rounded-lg" />
            )}
          </div>

          {/* History Section */}
          <Card className="gap-2">
            <CardHeader>
              <CardTitle className="text-base">History</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <BulkSuborderHistory
                bulk_suborder={bulk_suborder.data}
                refetch={bulk_suborder.refetch}
              />
              <BulkSuborderNoteForm
                bulk_suborder={bulk_suborder.data}
                onSuccess={bulk_suborder.refetch}
              />
            </CardContent>
          </Card>
        </div>
        <div className="grid gap-4">
          {/* Confirm Section */}
          <div className="relative">
            <BulkSuborderConfirmCard
              bulk_suborder={bulk_suborder.data}
              refetch={bulk_suborder.refetch}
            />
            {isCancelled && (
              <div className="absolute inset-0 bg-order-cancelled z-50 rounded-lg" />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
